import {
  ANTHROPIC_INTEGRATION_DESCRIPTION,
  ANTHROPIC_PROFILE_NAME,
  DATADOG_INTEGRATION_DESCRIPTION,
  DATADOG_PROFILE_NAME,
  EMAIL_PROFILE_NAME,
  JIRA_PROFILE_NAME,
  SERVICE_NOW_PROFILE_NAME,
  SLACK_PROFILE_NAME,
  TEAMS_PROFILE_NAME,
} from "app/views/notifications/integrations/consts";

export type IntegrationProfile =
  | typeof SLACK_PROFILE_NAME
  | typeof EMAIL_PROFILE_NAME
  | typeof JIRA_PROFILE_NAME
  | typeof SERVICE_NOW_PROFILE_NAME
  | typeof TEAMS_PROFILE_NAME
  | typeof DATADOG_PROFILE_NAME
  | typeof ANTHROPIC_PROFILE_NAME;

/**
 * Notification integrations deliver messages; policy extensions only feed data
 * into policies; cost sources import cost and usage data.
 */
export type IntegrationType =
  | "notification"
  | "policy-extension"
  | "cost-source";

interface IntegrationMeta {
  targetUrl: string;
  title: string;
  tooltipText?: string;
  type: IntegrationType;
}

export function useIntegrations() {
  const integrations = new Map<IntegrationProfile, IntegrationMeta>([
    [
      SLACK_PROFILE_NAME,
      { targetUrl: "slack", title: "Slack", type: "notification" },
    ],
    [
      EMAIL_PROFILE_NAME,
      { targetUrl: "email", title: "Email", type: "notification" },
    ],
    [
      JIRA_PROFILE_NAME,
      { targetUrl: "jira", title: "Jira", type: "notification" },
    ],
    [
      SERVICE_NOW_PROFILE_NAME,
      { targetUrl: "service-now", title: "ServiceNow", type: "notification" },
    ],
    [
      TEAMS_PROFILE_NAME,
      { targetUrl: "teams", title: "Microsoft Teams", type: "notification" },
    ],
    [
      DATADOG_PROFILE_NAME,
      {
        targetUrl: "datadog",
        title: "Datadog",
        tooltipText: DATADOG_INTEGRATION_DESCRIPTION,
        type: "policy-extension",
      },
    ],
    [
      ANTHROPIC_PROFILE_NAME,
      {
        targetUrl: "anthropic",
        title: "Anthropic",
        tooltipText: ANTHROPIC_INTEGRATION_DESCRIPTION,
        type: "cost-source",
      },
    ],
  ]);

  return integrations;
}

/** Only the integrations that can be picked as a notification transport. */
export function useNotificationIntegrations() {
  const integrations = useIntegrations();

  return new Map(
    [...integrations.entries()].filter(
      ([, meta]) => meta.type === "notification",
    ),
  );
}
